import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import Link from 'src/components/Link'
import Grid from 'src/components/Grid'
import { Logomark } from 'src/components/Logo'
import Section from 'src/components/Section'
import ThemeSelector from 'src/components/ThemeSelector'
import TextLockup from 'src/components/TextLockup'
import NewsletterForm from 'src/components/NewsletterForm'
import { mq, util, typography } from 'src/styles'
import { getSrc } from 'gatsby-plugin-image'

const Wrapper = styled(ThemeSelector)`
	position: relative;
	z-index: 2;
	${ ({ bgImage }) => bgImage ? `
		background-image: url(${ bgImage });
		background-size: cover;
		background-position: center;
	` : '' }
`

const FooterContent = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	p {
		text-align: center;
		${ mq.mediumAndBelow } {
			max-width: 30rem;
		}
	}
`

const StyledNewsletterForm = styled(NewsletterForm)`
	margin-top: 30px;
`

const LogoWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	// width: 100%;
	${ mq.extraLargeAndUp } {
		order: 2;
		width: 20%;
	}
`

const FooterLogo = styled(Logomark)`
	${ util.responsiveStyles('width', 110, 90, 86, 86) }
	margin-top: -1.5%;
	display: block;
`

const FooterBottom = styled(ThemeSelector)`
	position: relative;
	${ util.responsiveStyles('padding-top', 50, 40, 30, 26) }
	${ util.responsiveStyles('padding-bottom', 50, 40, 30, 26) }
	background: transparent;
`

const FooterBottomWrap = styled.div`
	display: flex;
	flex-direction: column;
	${ mq.extraLargeAndUp } {
		flex-direction: row;
		justify-content: center; 
		align-items: center;
	}
`

const Copyright = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	${ mq.largeAndBelow } {
		flex-direction: column;
	}
	p {
		${ typography.bodyTiny }
		margin: 0;
		max-width: none;
		.mobile-hide {
			${ mq.mediumAndBelow } {
				display: none;
			}
		}
	}
	${ mq.largerAndBelow } {
		justify-content: center;
		margin-top: 20px;
		order: 3;
	}
	${ mq.extraLargeAndUp } {
		order: 1;
		width: 40%;
	}
`

const SmallLinks = styled.div`
	text-align: right;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: wrap;
	order: 2;
	${ mq.largerAndBelow } {
		text-align: center;
		margin: 20px auto 0;
		justify-content: center;
	}
	${ mq.mediumAndBelow } {
		max-width: 20em;
	}
	p {
		margin: 0 0.65em;
		${ typography.bodyTiny }
		max-width: none;
		a {
			&:hover {
				opacity: 1;
			}
		}
		${ mq.mediumAndBelow } {
			text-align: center;
		}
	}
	${ mq.extraLargeAndUp } {
		margin-top: 0;
		order: 3;
		width: 40%;
	}
`

const Footer = ({ title, prevTheme, noEdge }) => {
	const { allContentfulSiteSettings } = useStaticQuery(
		graphql`
			query {
				allContentfulSiteSettings(filter: {internalName: {regex: "/^((?!PLACEHOLDER).)*$/"}}) {
					nodes {
						footerBackgroundImage {
							gatsbyImageData(width: 2000, quality: 80, placeholder: NONE)
				    }
					}
				}
			}
		`
	)

	const bgImage = getSrc(allContentfulSiteSettings?.nodes[0]?.footerBackgroundImage)

	return (
		<Wrapper setTheme="mainColor" bgImage={bgImage}>
			<Section roughEdgeClip={prevTheme} prevTheme={prevTheme} noEdge={!prevTheme || noEdge}>
				<Grid small='container'>
					<FooterContent>
						<svg width="58px" height="38px" viewBox="0 0 58 38" css={css`margin-bottom: 20px;`}>
								<g id="Symbols" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
										<g id="footer" transform="translate(-691.000000, -167.000000)" fill="#F4EBE6">
												<g id="Group-3" transform="translate(691.000000, 167.000000)">
														<path d="M17.3222079,21.8187236 C16.9045597,22.3075541 16.3230241,22.6303667 15.7641457,22.9562536 C15.1894073,23.2675372 14.6003193,23.5480767 13.9923503,23.7947977 C12.7326083,24.2498097 11.5521666,24.7271112 10.5235283,25.4488279 C9.82190941,25.9476501 9.19883557,26.4311004 8.64222295,26.9014844 C8.07352646,27.3595708 7.55920733,27.7922934 7.1075732,28.225016 C6.21034687,29.0966099 5.50344128,29.8974925 4.94078673,30.6922264 C3.82378526,32.2939916 3.22941058,33.787384 2.51872879,35.3714715 C6.49582798,32.9895758 9.04325592,32.0857006 11.9743463,28.1250978 C12.6880491,27.3403558 13.9107843,26.3550089 15.2498266,25.4872579 C15.9083968,25.0376261 16.5548831,24.5564817 17.1832436,24.1506601 C17.8085832,23.7394584 18.3999369,23.3820588 18.8447738,23.0446428 C19.3976103,22.6211434 19.9179713,22.1407676 20.3922627,21.6196559 L20.7389183,21.2207518 C20.8408758,21.0731803 20.9375467,20.9209972 21.0327071,20.7688141 C21.2207621,20.4621422 21.395978,20.1485528 21.5553338,19.8288147 C21.6263265,19.7327395 21.7146897,19.6328213 21.79399,19.5229113 C22.1640581,19.7988392 22.5363919,20.0763043 22.9321382,20.3522322 C23.9509583,21.0608827 25.0430368,21.7710703 26.3443171,22.3897944 C26.6343297,22.5319857 26.9250975,22.7026152 27.2521168,22.8563355 C27.5934858,23.0008325 27.9288128,23.1476354 28.3260695,23.281372 C28.7036901,23.4135714 29.2905123,23.5780521 29.8244677,23.4996548 C30.097865,23.4689107 30.3637098,23.37591 30.6159603,23.2483221 C30.743596,23.1883712 30.8621689,23.1061309 30.9845179,23.0315766 L31.2835934,22.8179054 C31.9791703,22.2683554 32.5909156,21.7856738 33.3386042,21.3045293 C34.0674117,20.8318395 34.7977298,20.3307114 35.5635442,19.884154 C36.1503665,19.5628786 36.7334125,19.2515951 37.3157034,18.9426173 C37.5271708,19.4022409 37.7288202,19.868782 37.994665,20.3030417 C38.2083982,20.6827308 38.4062714,21.0831721 38.65248,21.4467206 C38.9100171,21.8010458 39.1826591,22.1484536 39.4787136,22.4758778 C39.8578446,22.9331956 40.2528357,23.3705298 40.5760788,23.9062449 C40.9166925,24.4450345 41.2082156,24.9715264 41.4914309,25.5049358 C41.7806882,26.0314277 42.0518198,26.564837 42.3403218,27.0813371 C42.6333553,27.5916885 42.9603747,28.0482377 43.3546105,28.471737 C45.5032712,30.7491029 47.4291358,32.0149893 49.1541063,32.5368696 C50.0415145,32.7789791 50.8405595,32.8858146 51.6184577,32.8896577 C52.4107055,32.8727484 53.1546179,32.7858965 53.8872017,32.7013503 C52.286846,31.6168538 51.2091171,30.5853908 50.077766,29.5523905 C48.9313102,28.5462913 47.6927149,27.5547956 45.706431,26.5732916 C44.8832183,26.0836926 43.9134889,25.0422377 42.9377175,23.9731132 C42.4483213,23.4358609 41.9664775,22.8640215 41.6251086,22.3690422 C41.2573062,21.8694513 40.9491679,21.4036789 40.7596024,20.9824853 C40.1841087,20.020965 39.5293148,19.1616687 38.752927,18.3907615 C38.7272488,18.3477199 38.7045916,18.3016038 38.6811791,18.2554877 C39.2007849,18.0064608 39.7135936,17.761277 40.2082764,17.5245478 C40.9665384,17.1779086 41.6900593,16.8466414 42.3629791,16.5392008 C42.5321531,16.4638779 42.6983061,16.3893236 42.8606829,16.3170751 L42.9860529,16.2609672 C43.0457169,16.2325289 43.1189753,16.1971732 43.1824156,16.1556688 L43.4301346,16.0165519 C43.7284548,15.8551456 44.0116702,15.7014254 44.2782703,15.5561597 C46.9208586,14.0781393 49.663894,12.1773881 51.7898974,9.64868962 C52.2060352,9.19598342 52.7294173,8.68563211 53.0654995,8.06152781 C53.3623092,7.39976203 53.4672877,6.63039207 53.0466185,5.76110393 C51.7914079,7.66723531 50.1140176,9.19367761 48.3369355,10.4964569 C46.5545667,11.8061536 44.6611775,12.927543 42.8115922,14.1819005 C40.3170312,15.7536903 37.4357867,16.6675573 34.673115,17.9534274 C33.9639437,18.2408843 33.3046183,18.6297966 32.6158385,18.9725928 C31.9255482,19.338447 31.2624466,19.6658712 30.5766878,20.1108914 C30.2443817,20.319951 29.9166071,20.5420767 29.677951,20.6788878 C29.6371679,20.6965656 29.5556019,20.7488305 29.5510705,20.7419131 L29.5238818,20.7311527 C29.5080217,20.7211609 29.4959379,20.7157807 29.4853645,20.6981028 C29.4408053,20.668896 29.5397419,20.684268 29.3290296,20.5912672 C29.1160516,20.5021095 28.8132,20.3437776 28.5639705,20.2200328 C28.2006995,19.9871466 27.844981,19.758872 27.4968149,19.5359776 C27.1327887,19.2685043 26.7755597,19.0064112 26.4258831,18.7496984 C26.0762066,18.4722333 25.7333271,18.200917 25.3980001,17.9357495 C25.0604074,17.6559786 24.7303671,17.3823565 24.4078792,17.1148833 C23.1111303,16.0273123 21.8899056,15.0019981 20.7026667,14.0973543 C19.4542533,13.1719583 18.434678,12.4948205 17.4218998,11.7907816 C16.4212054,11.1005776 15.4763989,10.4695559 14.5806831,9.90617108 C10.9880015,7.65954929 8.01688338,6.49358102 4.9770383,5.89484056 C7.11437037,7.08309827 8.86955049,8.58725114 10.4955844,10.0130067 C12.1253945,11.4518284 13.6071774,12.8821956 15.1992254,14.2672152 C15.9665503,14.9512704 16.8630214,15.6591523 17.6537587,16.2886368 C18.4490275,16.9004435 19.2586459,17.5591349 20.1294388,18.2478017 C20.2721793,18.3600175 20.427759,18.4776135 20.5742758,18.5921351 C20.1966552,19.0256263 19.8084614,19.4468198 19.4180018,19.8564843 C18.7080752,20.5067211 17.9611418,21.1062301 17.3222079,21.8187236 M57.980608,21.4105963 L57.8408884,27.9875182 L57.4700651,34.5598284 C57.4700651,34.5598284 57.9118811,36.8894591 55.1960344,37.2699168 C54.1183055,37.5596795 53.0224508,37.699565 51.9356589,37.8294586 C47.5703659,38.2437347 43.2670026,37.7841111 38.9636393,37.6964906 C32.4247625,37.7433753 25.9561232,37.7894913 19.4867287,37.8356074 C13.0150684,37.7126312 6.54265292,37.5888864 0,37.5888864 C2.78381838,35.8649135 7.2382299,36.110866 10.5786609,36.110866 C15.0308067,35.9748235 19.5645185,35.9755921 24.0634892,35.8226405 C26.3163732,35.769607 28.5700124,35.7319455 30.8153439,35.7104247 C31.9383873,35.7035072 33.059165,35.6965898 34.1769217,35.6896724 L35.8505358,35.6881352 C36.4169665,35.6927468 37.0120965,35.6896724 37.5792825,35.7096561 C40.9952376,35.8088056 45.0697629,36.3176197 49.4305245,36.1900319 C50.519582,36.1577506 51.6177025,36.0578325 52.7180886,35.898732 C53.740685,35.7396315 55.1628038,35.3830005 55.4022152,35.2807765 C55.5842283,35.2800079 55.5290957,35.1047668 55.548732,34.9794848 L55.5728997,34.4921915 C55.5917807,33.8788476 55.611417,33.2639665 55.6310532,32.6467796 C55.6673048,31.4124058 55.7043116,30.170346 55.7413184,28.9275176 C55.7715281,26.4387864 55.7058221,23.940832 55.6831649,21.4874564 C55.6484238,19.0340809 55.5011518,16.6222098 55.4067467,14.3033395 C55.313852,11.9844692 55.1409018,9.75706241 54.997406,7.66954111 C54.918861,6.63116067 54.8433369,5.62659869 54.7700785,4.66277258 C54.7647918,4.11860281 54.3441225,3.58749927 53.8418873,3.66589661 C53.0458632,3.64821878 52.4326075,3.63361535 51.9175332,3.62208633 C51.3692282,3.61593752 50.9259017,3.61055731 50.4961696,3.6051771 C34.9064845,3.74275675 22.8415093,3.57673885 10.7931494,3.61670612 C8.25780527,3.61901193 5.72019545,3.62054913 3.13500546,3.62208633 L3.13047401,3.62439214 L3.09120148,3.76274038 L3.03304792,3.99408939 L2.92655894,4.49060587 C2.7823079,5.18619011 2.72037814,5.93096483 2.62748349,6.6511443 C2.50815541,8.10380088 3.12216636,9.96227898 3.1191454,11.6854832 C2.99906207,15.2225867 3.55642994,18.0956186 3.06929949,21.6496313 C2.59651861,25.1798174 2.51872879,29.4547782 0.760527703,32.7574582 C0.341368939,30.5915396 0.121593804,28.3164795 0.136698624,25.9745512 C0.11857284,23.6349286 0.234124715,21.2422726 0.331550806,18.7566158 C0.450123645,16.2771078 0.493172383,13.6846154 0.359494724,10.9837502 C0.252250499,9.63101179 0.120838563,8.23062011 0.120083322,6.74414507 C0.137453865,6.00551916 0.167663506,5.24460381 0.23790092,4.47753965 C0.277928694,4.09247037 0.326264119,3.70202087 0.397256774,3.30081096 C0.43879503,3.08406538 0.462962743,2.93265091 0.539242085,2.65134281 C0.611745223,2.39462995 0.691800771,2.13330549 0.805086923,1.88889026 C1.02108585,1.41235741 1.35943383,0.911229322 1.88885778,0.771343874 C2.14488449,0.725996393 2.41752649,0.674500102 2.69545519,0.672962899 L3.41444463,0.638375838 C4.20895818,0.595334162 5.01857655,0.551523884 5.84481022,0.506945005 C7.49878805,0.428547666 9.21922708,0.347844523 11.0136797,0.263298373 C12.8103981,0.195661453 14.6818853,0.124950127 16.6379596,0.0503957949 C18.5895024,0.0365609704 20.6256321,0.0227261458 22.7546566,0.00735411854 C31.2737752,-0.0410677674 41.2603272,0.145702364 53.2950928,0.68449192 C53.6538323,0.699863947 54.0125717,0.716004576 54.3652693,0.731376603 L54.7549737,0.792864712 L54.8523997,0.808236739 L54.8773227,0.812079746 C54.8863856,0.809005341 54.8818541,0.852047017 54.8946932,0.83667499 L54.9422734,0.848972612 L55.1303284,0.900468903 C55.3840894,0.958114005 55.6167037,1.07878442 55.8425207,1.21098385 C56.2775395,1.49767216 56.6559153,1.87736124 56.933844,2.32853024 C57.2193251,2.77278182 57.4292821,3.27852152 57.5221767,3.80962506 C57.5493654,3.9410559 57.5629598,4.07709834 57.5757988,4.21006637 L57.5946799,4.54517657 L57.6158266,4.9571469 L57.7668748,8.24983514 C57.8620352,10.4426548 57.9360488,12.6331687 57.9534194,14.8313686 C57.9685242,17.0272627 58.0312092,19.2139336 57.980608,21.4105963" id="Fill-1"></path>
												</g>
										</g>
								</g>
						</svg>
						<TextLockup
							eyebrow="Stay Updated"
							text="Sign up to be the first to know about new flavors, events, and more."
							setTheme='mainColor'
							underlineColor='pink2'
							alignment='center'
						/>
						<StyledNewsletterForm
							size='small'
						/>
					</FooterContent>
				</Grid>
			</Section>
			<FooterBottom setTheme="mainColor">
				<Grid small='container' large='1 [24] 1'>
					<FooterBottomWrap>
						<LogoWrapper>
							<FooterLogo />
						</LogoWrapper>
						<Copyright>
							<p>© <span>{title}</span>, LLC.</p>
							<p>3146 John P Curci Dr. Hallandale Beach, FL 33009-3826</p>
						</Copyright>
						<SmallLinks>
							<p className="sm"><Link to="/terms-and-conditions">Terms & Conditions</Link></p>
							<p className="sm"><Link to="/privacy-policy">Privacy Policy</Link></p>
							<p className="sm"><Link to="https://www.responsibility.org/" target="_blank" external>Drink Responsibly</Link></p>
							<p className="sm site-credit"><Link to="https://hyperlinknyc.com/" target="_blank" external>Site Credit</Link></p>
						</SmallLinks>
					</FooterBottomWrap>
				</Grid>
			</FooterBottom>
		</Wrapper>
	)
}

export default Footer
